import * as React from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import { FlowPanel } from "./panels/FlowPanel";
import { VariablesPanel } from "./panels/VariablesPanel";
import { GoToQuestionPanel } from "./panels/GoToQuestionPanel";
import { useThunkDispatch } from "../../redux/thunks";
import { mobilePreviewToggle } from "../../redux/actions";
import { StoreState } from "../../models/states/StoreState";
import { SettingsPanel } from "./panels/SettingsPanel";
import { DockPosition } from "./shared/DockPosition";
import { Panel } from "./panels/Panel";
import DockedDebugPanel from "./DockedDebugPanel";
import FloatingDebugPanel from "./FloatingDebugPanel";
import useLocalStorage from "../../hooks/useLocalStorage";
import { SurveyMode } from "../../models/enums/SurveyMode";
import { AccessibilityPanel } from "./panels/AccessibilityPanel";

export function DebugPanel() {
    const [selected, setSelected] = React.useState("gotoquestion");
    const dispatch = useThunkDispatch();
    const [position, setPosition] = useLocalStorage<DockPosition>("debugDockPosition", "left");
    const mobilePreview = useSelector((state: StoreState) => state.mobilePreview);
    const { t } = useTranslation();

    const applicationMode = useSelector((state: StoreState) => state.application.mode);
    const displayInThemeEditor = useSelector(
        (state: StoreState) => state.application.displayInThemeEditor,
    );

    const hideAccessibilityPanel = useSelector(
        (state: StoreState) => state.application.hideAccessibilityPanel,
    );

    const title =
        {
            [SurveyMode.Preview]: t("debug.previewMode"),
            [SurveyMode.Test]: t("debug.testMode"),
            [SurveyMode.Normal]: t("debug.previewMode"),
            [SurveyMode.Simulation]: t("debug.previewMode"),
        }[applicationMode] || t("debug.previewMode");

    const visiblePanelsBySurveyMode = {
        [SurveyMode.Preview]: [
            "gotoquestion",
            "flow",
            "variables",
            "accessibility",
            "mobilepreview",
            "settings",
        ],
        [SurveyMode.Test]: ["gotoquestion", "mobilepreview", "settings"],
        [SurveyMode.Normal]: [],
        [SurveyMode.Simulation]: [],
    };

    const visiblePanelsInThemeEditor = ["gotoquestion", "accessibility", "settings"];

    if (hideAccessibilityPanel) {
        const indexOfAccessibility =
            visiblePanelsBySurveyMode[SurveyMode.Preview].indexOf("accessibility");
        if (indexOfAccessibility > -1) {
            visiblePanelsBySurveyMode[SurveyMode.Preview].splice(indexOfAccessibility, 1);
        }
    }
    const visiblePanels =
        applicationMode === SurveyMode.Preview && displayInThemeEditor
            ? visiblePanelsInThemeEditor
            : visiblePanelsBySurveyMode[applicationMode] || [];

    const panels = React.useMemo<Panel[]>(
        () =>
            [
                {
                    id: "gotoquestion",
                    index: 0,
                    title: t("debug.panels.goToQuestion.title"),
                    active: selected === "gotoquestion",
                    content: <GoToQuestionPanel />,
                    icon: <FontAwesomeIcon icon={["far", "link"]} />,
                },
                {
                    id: "flow",
                    index: 1,
                    title: t("debug.panels.flow.title"),
                    content: <FlowPanel />,
                    active: selected === "flow",
                    icon: <FontAwesomeIcon icon={["far", "code-branch"]} />,
                },
                {
                    id: "variables",
                    index: 2,
                    title: t("debug.panels.variables.title"),
                    content: <VariablesPanel />,
                    active: selected === "variables",
                    icon: (
                        <div className="fa-layers fa-fw">
                            <FontAwesomeIcon transform="grow-3" icon={["far", "brackets-curly"]} />
                            <span
                                className="fa-layers-text"
                                style={{
                                    fontStyle: "italic",
                                    fontSize: 16,
                                    fontWeight: 600,
                                    transform: "translate(-50%, -52%)",
                                }}
                            >
                                x
                            </span>
                        </div>
                    ),
                },
                {
                    id: "accessibility",
                    index: 3,
                    title: t("debug.panels.accessibility.title"),
                    content: <AccessibilityPanel title={t("debug.panels.accessibility.title")} />,
                    active: selected === "accessibility",
                    icon: <FontAwesomeIcon icon={["far", "eye"]} />,
                },
                {
                    id: "mobilepreview",
                    index: 4,
                    title: t("debug.toggleMobilePreview"),
                    exclude: position !== "floating",
                    active: mobilePreview,
                    icon: <FontAwesomeIcon icon={["far", "mobile-android"]} />,
                    onClick: () => dispatch(mobilePreviewToggle()),
                },
                {
                    id: "settings",
                    index: -1,
                    exclude: true,
                    title: t("debug.panels.settings.title"),
                    content: (
                        <SettingsPanel onChangeDockPosition={setPosition} dockPosition={position} />
                    ),
                    active: selected === "settings",
                    icon: <FontAwesomeIcon icon={["far", "cog"]} />,
                },
            ].filter((panel) => visiblePanels.includes(panel.id)),
        [
            t,
            selected,
            position,
            mobilePreview,
            setPosition,
            dispatch,
            visiblePanels,
            applicationMode,
            displayInThemeEditor,
        ],
    );

    if (position === "left" || position === "right")
        return (
            <DockedDebugPanel
                title={title || ""}
                position={position}
                panels={panels}
                selected={selected}
                onSelectedChanged={setSelected}
            />
        );

    return (
        <FloatingDebugPanel
            title={title || ""}
            panels={panels}
            selected={selected}
            onSelectedChanged={setSelected}
        />
    );
}
